// Browser push-socket wiring.
//
// One push socket per origin, not per tab. The tab that wins an
// exclusive Web Lock ("practicemd-push") becomes the leader and owns
// the single WebSocket (via the connectPush WASM binding); every other
// tab queues on the same lock and takes over automatically if the
// leader goes away. Inbound OOBM frames fan out to all tabs over a
// BroadcastChannel, and every tab re-dispatches them as a window
// CustomEvent (PUSH_FRAME_EVENT) — the same delivery shape as
// WASM_DEVICE_SAVED_EVENT in deviceStore.ts.
//
// Mounted by DeviceProvider once the device is active (seedKid set);
// see useDevice.tsx. Only the leader calls prepareDeviceArgs, so the
// unlock prompt fires at most once across the origin, matching how the
// heartbeat already behaves.

import { apiGraphqlEndpoint, prepareDeviceArgs } from './ops'
import { loadPracticemdWasm } from './loader'

// Window CustomEvent carrying a single inbound push frame. detail is the
// parsed frame object (or { raw } if it wasn't JSON). Consumers listen
// with window.addEventListener(PUSH_FRAME_EVENT, e => (e as CustomEvent).detail).
export const PUSH_FRAME_EVENT = 'practicemd-push-frame'

// Window CustomEvent fired (leader-local) each time the push socket is
// (re)established — the reconnect catch-up signal (Push-Architecture
// §16.5). Carries no detail; consumers pull authoritative state on it,
// since OOBMs missed while disconnected are not replayed (§8). Dispatched
// only in the leader tab: the keyring is origin-shared, so a single pull
// converges every tab.
export const PUSH_CONNECTED_EVENT = 'practicemd-push-connected'

const LOCK_NAME = 'practicemd-push'
const CHANNEL_NAME = 'practicemd-push'

// Module-singleton refcount so React StrictMode's double-mount (and any
// future multi-consumer use within a tab) opens exactly one socket.
let refCount = 0
let controller: AbortController | null = null
let channel: BroadcastChannel | null = null

function hasLocks(): boolean {
    return (
        typeof navigator !== 'undefined' &&
        typeof (navigator as any).locks?.request === 'function'
    )
}

function makeChannel(): BroadcastChannel | null {
    if (typeof BroadcastChannel === 'undefined') return null
    try {
        return new BroadcastChannel(CHANNEL_NAME)
    } catch {
        return null
    }
}

// Re-dispatch a frame to this tab's app code as a window CustomEvent.
function dispatchLocal(frame: unknown): void {
    if (typeof window === 'undefined') return
    try {
        window.dispatchEvent(new CustomEvent(PUSH_FRAME_EVENT, { detail: frame }))
    } catch {
        /* non-fatal */
    }
}

// Leader-side: the socket just (re)established. Signal this tab's app code
// to run reconnect catch-up (§16.5) — pull authoritative state, since any
// OOBM missed while disconnected was dropped, not queued (§8). Leader-local
// (not fanned across tabs): the keyring is origin-shared, so one pull
// converges every tab.
function dispatchConnected(): void {
    if (typeof window === 'undefined') return
    try {
        window.dispatchEvent(new CustomEvent(PUSH_CONNECTED_EVENT))
    } catch {
        /* non-fatal */
    }
}

// Leader-side: a frame just arrived on the socket. Parse it, fan it out
// to the other tabs (BroadcastChannel does not echo to the sender), and
// dispatch it locally for this (leader) tab.
function deliverFrame(frameJSON: string): void {
    let frame: unknown
    try {
        frame = JSON.parse(frameJSON)
    } catch {
        frame = { raw: frameJSON }
    }
    try {
        channel?.postMessage(frame)
    } catch {
        /* non-fatal */
    }
    dispatchLocal(frame)
}

// The reconnect-pacing rule (escalate on fast drops, forgive a connection that
// lived a healthy stint) is device.NextReconnectAttempt in the shared library,
// reached through the WASM bindings below. It used to be implemented here too;
// two copies of a rule whose whole point is a subtle reset condition is how one
// of them drifts back into the bug it was written to fix. Its scenarios now
// live in common/device/push_consumer_test.go, ported verbatim.
//
// What stays here is the WAITING, which is genuinely browser-shaped: an
// abortable timer, so a leader that loses its lock stops immediately rather
// than after a 30s sleep.
async function backoff(
    wasm: Awaited<ReturnType<typeof loadPracticemdWasm>>,
    attempt: number,
    signal: AbortSignal
): Promise<void> {
    const delay = wasm.reconnectBackoffMs(attempt)
    return new Promise<void>((resolve) => {
        const t = setTimeout(resolve, delay)
        signal.addEventListener(
            'abort',
            () => {
                clearTimeout(t)
                resolve()
            },
            { once: true }
        )
    })
}

// One connectPush lifetime. Resolves true if the socket dropped on its
// own (caller should reconnect), false if it closed because we aborted
// or the caller stopped it cleanly.
function connectOnce(
    wasm: Awaited<ReturnType<typeof loadPracticemdWasm>>,
    endpoint: string,
    seedHex: string,
    signal: AbortSignal
): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
        let settled = false
        const settle = (dropped: boolean) => {
            if (settled) return
            settled = true
            signal.removeEventListener('abort', onAbort)
            resolve(dropped)
        }
        const onAbort = () => {
            try {
                handle.stop()
            } catch {
                /* non-fatal */
            }
            settle(false)
        }
        const handle = wasm.connectPush(
            endpoint,
            seedHex,
            (frameJSON) => deliverFrame(frameJSON),
            // error === "" means a clean caller stop(); anything else is a
            // dropped socket we should reconnect after backing off.
            (status) => settle(status.error !== ''),
            // Socket (re)established: fire reconnect catch-up (§16.5).
            () => dispatchConnected()
        )
        if (handle.error) {
            // Immediate failure (e.g. ticket mint failed). Treat as a drop
            // so the loop backs off and retries.
            settle(true)
            return
        }
        signal.addEventListener('abort', onAbort, { once: true })
    })
}

// Leader loop: hold the seed for the connection's lifetime, (re)dial
// until told to stop. Returning releases the Web Lock so a queued tab
// can take over.
async function runLeader(signal: AbortSignal): Promise<void> {
    let seedHex: string
    try {
        // Only the leader unlocks; the prompt (if any) fires here, once.
        ;({ seedHex } = await prepareDeviceArgs())
    } catch (e) {
        // No device / unlock cancelled — give up leadership; another tab
        // (or a later seedKid change re-mounting us) can try again.
        console.warn('[push] not starting leader:', e)
        return
    }
    if (signal.aborted) return

    const wasm = await loadPracticemdWasm()
    const endpoint = apiGraphqlEndpoint()

    let attempt = 0
    while (!signal.aborted) {
        const dialedAt = Date.now()
        const dropped = await connectOnce(wasm, endpoint, seedHex, signal)
        if (signal.aborted || !dropped) break
        attempt = wasm.nextReconnectAttempt(attempt, Date.now() - dialedAt)
        await backoff(wasm, attempt, signal)
    }
}

// Every tab requests the lock. The callback runs only in the tab that
// currently holds it (the leader); the rest stay queued and run later if
// the leader releases. The signal both aborts a queued request and stops
// an active leader.
function beginLeaderElection(signal: AbortSignal): void {
    if (!hasLocks()) {
        // Degraded path: no Web Locks (old browser). Connect directly in
        // this tab. Other tabs do the same — multiple sockets, but
        // functional. Fan-out still works for any tabs that do have a
        // BroadcastChannel.
        void runLeader(signal)
        return
    }
    ;(navigator as any).locks
        .request(LOCK_NAME, { mode: 'exclusive', signal }, async () => {
            await runLeader(signal)
        })
        .catch((e: any) => {
            // AbortError on stop() is expected; ignore.
            if (e && e.name !== 'AbortError') {
                console.warn('[push] lock request failed:', e)
            }
        })
}

// Start the push socket for this origin. Idempotent within a tab via
// refcount. Returns a stop handle; the socket actually closes only when
// the last caller stops.
export function startPush(): () => void {
    if (typeof window === 'undefined') {
        return () => {}
    }

    refCount++
    if (refCount === 1) {
        controller = new AbortController()
        channel = makeChannel()
        if (channel) {
            // Followers (and the leader, for frames from a future leader)
            // receive fanned-out frames here.
            channel.onmessage = (e) => dispatchLocal(e.data)
        }
        beginLeaderElection(controller.signal)
    }

    let stopped = false
    return () => {
        if (stopped) return
        stopped = true
        refCount--
        if (refCount === 0) {
            controller?.abort()
            controller = null
            try {
                channel?.close()
            } catch {
                /* non-fatal */
            }
            channel = null
        }
    }
}
